import React from 'react'
import {omit} from 'lodash'
import {strftime} from '@fotolog/xutil'
import {useLocation} from 'wouter'
import {apiReply, toast, useAppNav, useForm, getDataAttr} from '../lib'
import {useSuspense, useController} from '@data-client/react'
import {Acl, AclResource, TripResource} from '../lib/model'
import {AppMsg, Field, ActionHeader, PanelProps, SubmitButton} from '.'
import {AddAclSchema, UpdateAclSchema} from '../lib/schema/Acl'
import {AddAclForm, UpdateAclForm} from '../lib/form/Acl'
import {SelectTrip} from './Trip'
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {IconCopy, IconDelete, IconTeam, IconToken, IconTripMap, IconGlobe, IconUpdate} from '../Icons'

export function AddAcl({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const trips = useSuspense(TripResource.getList, {scope: session.euid(), tags: []})

  const form = useForm(AddAclSchema, {defaults: {tripIds: []}})

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit(async (data: AddAclSchema) => {
    const msg = toast('loading', 'Adding Access Rule')
    const tripIds = data.tripIds?.map(i => i.value)
    const reply = await apiReply(() => api.fetch(AclResource.getList.push, {...data, userId: session.euid(), tripIds}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed()
    api.invalidate(AclResource.getList, {scope: session.euid()})
    nav.to(params['home']) // nav('BACK')
  })

  // TODO: SelectMember for include & exclude lists
  return (
    <form onSubmit={onSubmit}>
      <Box my={5}>
        <Field form={form} attrs={AddAclForm.name} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={AddAclForm.team} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={AddAclForm.token} />
      </Box>
      <Box my={5}>
        <SelectTrip form={form} name="tripIds" trips={trips} attrs={AddAclForm.trips} />
      </Box>
      <Box mt={3} textAlign="center">
        <SubmitButton form={form}>Add Rule</SubmitButton>
      </Box>
    </form>
  )
}

export function UpdateAcl({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const acl = useSuspense(AclResource.get, {id: params['id']})
  const trips = useSuspense(TripResource.getList, {scope: session.euid(), tags: []})
  const form = useForm(UpdateAclSchema, {defaults: omit(acl, ['tripIds'])})

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit(async (data: UpdateAclSchema) => {
    const msg = toast('loading', 'Updating Access Rule')
    let tripIds: number[] = []
    if (data.tripIds) {
      tripIds = data.tripIds.map(i => i.value)
    }
    const reply = await apiReply(() => api.fetch(AclResource.update, {id: acl.id}, {...data, tripIds}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed()
    nav.back()
  })

  return (
    <form onSubmit={onSubmit}>
      <Box my={5}>
        <Field form={form} attrs={UpdateAclForm.name} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={UpdateAclForm.team} />
      </Box>
      <Box my={5}>
        <Field form={form} attrs={UpdateAclForm.token} />
      </Box>
      <Box my={5}>
        <SelectTrip form={form} name="tripIds" trips={trips} defvals={acl.tripIds} attrs={UpdateAclForm.trips} />
      </Box>
      <Box mt={3} textAlign="center">
        <SubmitButton form={form}>Save Changes</SubmitButton>
      </Box>
    </form>
  )
}

// TODO: see if Trips.TripSummary should take id instead of trip
type TripSummaryProps = {
  id: number
  size?: number
  truncate?: boolean
}
function TripSummary({id, size = 100, truncate = true}: TripSummaryProps) {
  const trip = useSuspense(TripResource.get, {id})
  return (
    <Box display="flex" alignItems="center" mt={2}>
      <Box
        mr={1}
        className="app-profile-img"
        sx={{
          width: size,
          height: size,
          borderRadius: '10%',
          backgroundImage: `url(${trip.cover?.thumbnail})`,
        }}
      />
      <div className={truncate ? 'app-truncate' : ''} title={trip.title} style={{width: '100%'}}>
        <div>{trip.title}</div>
        <Box className="app-flex" color="text.secondary" fontSize="small">
          <Button href={`/trip/${id}-${trip.slug}`}>
            <IconTripMap color="primary" sx={{mr: 1}} /> {trip.fotos.length} Fotos
          </Button>
          {strftime(trip.happened, 'PPP')}
        </Box>
      </div>
    </Box>
  )
}

export function ViewAcl({session, params}: PanelProps) {
  const acl = useSuspense(AclResource.get, {id: params['id']})
  const copyToken = () => {
    if (acl.token) {
      navigator.clipboard.writeText(acl.token)
      toast('info', 'Token Copied to Clipboard')
    }
  }
  return (
    <>
      <ActionHeader title={acl.name}>
        <Button href={`../update`} color="primary">
          <IconUpdate />
        </Button>
        <Divider orientation="vertical" variant="middle" flexItem />
        <Button href={`../delete`} color="primary">
          <IconDelete />
        </Button>
      </ActionHeader>
      <Paper elevation={3} sx={{py: 1, px: 2, mt: 3}}>
        <dl className="app-keyval">
          <dt>Visible to Team?</dt>
          <dd>{acl.team ? 'Yes' : 'No'}</dd>
          <dt>Token Access?</dt>
          <dd>
            {acl.token ? (
              <Button onClick={copyToken} sx={{fontSize: 'small'}} className="app-truncate">
                <IconCopy /> &nbsp; Click to Copy
              </Button>
            ) : (
              'No'
            )}
          </dd>
          <dt>Created</dt>
          <dd>{strftime(acl.created, 'PPP')}</dd>
        </dl>
      </Paper>
      <Typography variant="h3" mt={3}>
        Specific Trips
      </Typography>
      {(acl.tripIds || []).map(id => (
        <TripSummary id={id} size={50} key={id} />
      ))}
      {!acl.tripIds?.length && (
        <AppMsg severity="warning" size="small">
          Applicable To All Trips
        </AppMsg>
      )}
    </>
  )
}

type AclRowProps = {
  acl: Acl
}
export function AclRow({acl}: AclRowProps) {
  return (
    <TableRow data-id={acl.id} hover className="app-clickable" sx={{'&:last-child td, &:last-child th': {border: 0}}}>
      <TableCell>
        <Typography variant="h5" className="app-truncate">
          {acl.name}
        </Typography>
        <Chip
          variant="outlined"
          icon={<IconGlobe color="secondary" />}
          label={`${acl.tripIds?.length || 'All'} Trips`}
          sx={{m: 1, p: 1, fontSize: 14}}
        />
        {acl.team && (
          <Chip
            variant="outlined"
            icon={<IconTeam color="secondary" />}
            label="Team Access"
            sx={{m: 1, p: 1, fontSize: 14}}
          />
        )}
        {acl.token && (
          <Chip
            variant="outlined"
            icon={<IconToken color="secondary" />}
            label="Token Access"
            sx={{m: 1, p: 1, fontSize: 14}}
          />
        )}
      </TableCell>
      <TableCell align="right" className="app-hide-sm">
        {strftime(acl.created, 'PPP')}
      </TableCell>
    </TableRow>
  )
}

type AclsTableProps = {
  acls: Acl[]
}
export function AclsTable({acls}: AclsTableProps) {
  const setLocation = useLocation()[1]
  const onDetails = (evt: React.SyntheticEvent<HTMLElement>) => {
    const id = getDataAttr('id', evt.target as HTMLElement)
    setLocation(`/acl/${id}/view`)
  }
  if (acls.length === 0) {
    return (
      <AppMsg severity="warning">
        No Access Rules
        <br />
        Have Been Defined
      </AppMsg>
    )
  }
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left">Description</TableCell>
            <TableCell align="right" className="app-hide-sm">
              Created
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody onClick={onDetails}>
          {acls.map(i => (
            <AclRow key={i.id} acl={i} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export function DeleteAcl({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const acl = useSuspense(AclResource.get, {id: params['id']})

  const onSubmit = async () => {
    const msg = toast('loading', 'Deleting Access Rule')
    const reply = await apiReply(() => api.fetch(AclResource.delete, {id: params['id']}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed()
    nav.to(params['home'])
  }

  return (
    <>
      <Typography variant="h2" my={3}><span className='app-subdued'>Access Rule:</span> {acl.name}</Typography>
      <AppMsg severity="error" size="medium">
        This Action Cannot Be Undone.
        <br />
        Are you sure?
      </AppMsg>
      <Box mt={3} display="flex" justifyContent="center">
        <Button color="secondary" sx={{mr: 2}} onClick={() => nav.back()}>
          Cancel
        </Button>
        <Button color="primary" onClick={onSubmit}>
          Yes, Delete
        </Button>
      </Box>
    </>
  )
}
